import { BaseReduxService } from '../Api/BaseReduxService';
import { Lazy } from '../../../Helpers/Lazy';
import { II18nService, I18nServiceName } from '../I18n/I18nService';
import { IMediaDataService, MediaDataServiceName } from '../../../DataServices/MediaDataService';
import { IoC } from '../../ServicesContainer';
import { ExportFileType, ExportUtils } from '../../../Utils/ExportUtils';
import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';

export interface IDownloadService {
    downloadFile: (
        url: string,
        fileTitle: string,
        fileType: ExportFileType,
        cts: XHRRequestCanceler
    ) => void;
}

class DownloadService extends BaseReduxService implements IDownloadService {
    private readonly i18nService: Lazy<II18nService>;
    private readonly mediaDataService: Lazy<IMediaDataService>;

    constructor() {
        super();
        this.i18nService = IoC.getLazy<II18nService>(I18nServiceName);
        this.mediaDataService = IoC.getLazy<IMediaDataService>(MediaDataServiceName);
    }

    public downloadFile = async (
        url: string,
        fileTitle: string,
        fileType: ExportFileType,
        cts: XHRRequestCanceler
    ) => {
        const fileNameWithExtension = fileTitle + fileType;

        const getProgressValueMessage = (progress: number): string => {
            return this.i18nService.value().formatMessage('Control_PdfDownloadProgress', {
                value: Math.round(progress * 100),
            });
        };

        const downloadToastId = this.toastService.value().showDownloadInfo({
            fileName: fileNameWithExtension,
            content: getProgressValueMessage(0),
        });

        try {
            const response: ArrayBuffer = await this.mediaDataService
                .value()
                .getFileData(url, cts, (progress: number) => {
                    if (downloadToastId) {
                        this.toastService.value().updateDownloadInfo(downloadToastId, {
                            fileName: fileNameWithExtension,
                            content: getProgressValueMessage(progress),
                        });

                        if (progress >= 1) {
                            this.toastService.value().updateDownloadInfo(downloadToastId, {
                                fileName: fileNameWithExtension,
                                content: getProgressValueMessage(progress),
                                success: true,
                            });
                            setTimeout(() => {
                                this.toastService.value().dismiss(downloadToastId);
                            }, 1000);
                        }
                    }
                });
            ExportUtils.saveToFile(response, fileTitle, fileType);
        } catch (err) {
            this.toastService.value().updateDownloadInfo(downloadToastId, {
                fileName: fileNameWithExtension,
                content: getProgressValueMessage(0),
                failed: true,
            });
            setTimeout(() => {
                this.toastService.value().dismiss(downloadToastId);
            }, 1000);
            Promise.reject(err);
        }
    };
}

const DownloadServiceName = 'DownloadService';
export { DownloadService, DownloadServiceName };
